import { useCallback, useEffect, useState } from 'react';
import type { CustomCategoryDTO } from '@astral/shared';
import { api } from '../lib/api.js';

/**
 * The signed-in user's own project categories.
 *
 * Loaded once per screen and refreshed explicitly after a create or a delete,
 * mirroring how `useProjects` works — there is no cache layer in this app and
 * inventing one for a list of a handful of rows would be noise.
 */
export function useCustomCategories(): {
  categories: CustomCategoryDTO[];
  loading: boolean;
  reload: () => void;
} {
  const [categories, setCategories] = useState<CustomCategoryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listCustomCategories()
      .then((rows) => {
        if (!cancelled) setCategories(rows);
      })
      .catch(() => {
        // A failed load leaves the built-ins working; the user is not blocked.
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return { categories, loading, reload: useCallback(() => setNonce((n) => n + 1), []) };
}
